import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Server, Users, Award, Globe, Shield, Clock, Zap, Heart } from 'lucide-react';
import { getStorageUrl } from '../utils/imageUrlUpdater';

const About: React.FC = () => {
  // Set page title and canonical URL
  useEffect(() => {
    document.title = 'Giới Thiệu - VMon Cloud';
    
    // Set canonical URL
    const existingCanonical = document.querySelector('link[rel="canonical"]');
    if (existingCanonical) {
      existingCanonical.remove();
    }
    
    const canonical = document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = 'https://vmon.vn/gioi-thieu';
    document.head.appendChild(canonical);
    
    return () => {
      if (canonical.parentNode) {
        canonical.parentNode.removeChild(canonical);
      }
      document.title = 'VMon Cloud - Dịch vụ VPS và Hosting chất lượng cao';
    };
  }, []);

  const stats = [
    { icon: Users, label: 'Khách hàng', value: '5,000+' },
    { icon: Server, label: 'Máy chủ', value: '100+' },
    { icon: Globe, label: 'Quốc gia', value: '15+' },
    { icon: Award, label: 'Năm kinh nghiệm', value: '8+' }
  ];

  const values = [
    {
      icon: Shield,
      title: 'Bảo mật',
      description: 'Cam kết bảo vệ dữ liệu khách hàng với các tiêu chuẩn bảo mật cao nhất'
    },
    {
      icon: Zap,
      title: 'Hiệu suất',
      description: 'Cung cấp hiệu suất tối ưu với công nghệ tiên tiến và hạ tầng hiện đại'
    },
    {
      icon: Clock,
      title: 'Hỗ trợ 24/7',
      description: 'Đội ngũ kỹ thuật chuyên nghiệp sẵn sàng hỗ trợ khách hàng mọi lúc'
    },
    {
      icon: Heart,
      title: 'Tận tâm',
      description: 'Luôn đặt lợi ích khách hàng lên hàng đầu trong mọi quyết định'
    }
  ];

  const team = [
    {
      name: 'Nguyễn Văn A',
      position: 'CEO & Founder',
      description: '10+ năm kinh nghiệm trong lĩnh vực hosting và cloud computing'
    },
    {
      name: 'Trần Thị B',
      position: 'CTO',
      description: 'Chuyên gia hệ thống với chứng chỉ AWS và Azure Solutions Architect'
    },
    {
      name: 'Lê Văn C',
      position: 'Head of Support',
      description: 'Dẫn dắt đội ngũ support 24/7 với kinh nghiệm 8+ năm'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Về VMON</h1>
            <p className="text-xl mb-8 max-w-3xl mx-auto">
              Chúng tôi là nhà cung cấp dịch vụ VPS và hosting hàng đầu, 
              cam kết mang đến giải pháp tối ưu cho doanh nghiệp và cá nhân.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <stat.icon className="h-8 w-8 text-blue-600" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Câu Chuyện Của Chúng Tôi</h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  VMon Cloud chính thức được thành lập vào tháng 8 năm 2021, mặc dù chúng tôi đã kinh doanh từ trước đó và có chỗ đứng nhất định trên thị trường máy chủ ảo. Mục tiêu chính của chúng tôi không thay đổi kể từ ngày đầu tiên: cam kết cung cấp các giải pháp máy ảo hàng đầu, cân bằng giữa chi phí hoạt động và hiệu suất mạnh mẽ, mang lại giá trị lâu dài cho khách hàng.
                </p>
                <p>
                  Với nhiệt huyết, khát khao trở thành nhà cung cấp giải pháp và công nghệ số hàng đầu Việt Nam, VMon Cloud có đội ngũ kỹ thuật viên nhiều năm kinh nghiệm luôn sẵn sàng phục vụ.
                </p>
                <p>
                  Chúng tôi tin rằng, điều quan trọng nhất là sự hài lòng của khách hàng. Với VMon Cloud, bạn không chỉ là khách hàng mà còn là thành viên trong gia đình chúng tôi, chúng tôi kinh doanh bằng cả trái tim.
                </p>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Tầm Nhìn & Sứ Mệnh</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Tầm Nhìn</h4>
                  <p className="text-gray-600 text-sm">
                    Trở thành nhà cung cấp giải pháp và công nghệ số hàng đầu Việt Nam.
                  </p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Sứ Mệnh</h4>
                  <p className="text-gray-600 text-sm">
                    Mang đến công nghệ hiện đại, không ngừng cải tiến. Đồng hành cùng sự thành công trong kinh doanh của khách hàng.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Giá Trị Cốt Lõi</h2>
            <p className="text-lg text-gray-600">Những nguyên tắc định hướng mọi hoạt động của chúng tôi</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <value.icon className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Infrastructure Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Hạ Tầng Toàn Cầu</h2>
            <p className="text-lg text-gray-600">Mạng lưới datacenter hiện đại trên khắp thế giới</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Châu Á - Thái Bình Dương</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Việt Nam (Hà Nội, TP.HCM)</li>
                <li>• Singapore</li>
                <li>• Tokyo, Japan</li>
                <li>• Hong Kong</li>
              </ul>
            </div>
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Bắc Mỹ</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• New York, USA</li>
                <li>• Los Angeles, USA</li>
                <li>• Toronto, Canada</li>
                <li>• Vancouver, Canada</li>
              </ul>
            </div>
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Châu Âu</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• London, UK</li>
                <li>• Frankfurt, Germany</li>
                <li>• Amsterdam, Netherlands</li>
                <li>• Paris, France</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Các Dịch Vụ Tại VMON Cloud</h2>
            <p className="text-lg text-gray-600">Chúng tôi cung cấp đa dạng dịch vụ VPS tại nhiều quốc gia</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            <Link to="/vps-us" className="bg-white p-4 rounded-lg shadow text-center hover:shadow-md transition-shadow">
              <div className="h-12 flex items-center justify-center mb-2">
                <img src={getStorageUrl('flag/usa-flag.svg')} alt="USA Flag" className="h-8" />
              </div>
              <h3 className="text-sm font-medium text-gray-900">VPS US - Mỹ</h3>
            </Link>
            
            <Link to="/vps-singapore" className="bg-white p-4 rounded-lg shadow text-center hover:shadow-md transition-shadow">
              <div className="h-12 flex items-center justify-center mb-2">
                <img src={getStorageUrl('flag/singapore-flag.svg')} alt="Singapore Flag" className="h-8" />
              </div>
              <h3 className="text-sm font-medium text-gray-900">VPS Singapore</h3>
            </Link>
            
            <Link to="/vps-australia" className="bg-white p-4 rounded-lg shadow text-center hover:shadow-md transition-shadow">
              <div className="h-12 flex items-center justify-center mb-2">
                <img src={getStorageUrl('flag/australia-flag.svg')} alt="Australia Flag" className="h-8" />
              </div>
              <h3 className="text-sm font-medium text-gray-900">VPS Australia</h3>
            </Link>
            
            <Link to="/vps-europe" className="bg-white p-4 rounded-lg shadow text-center hover:shadow-md transition-shadow">
              <div className="h-12 flex items-center justify-center mb-2">
                <img src={getStorageUrl('flag/eu-flag.svg')} alt="EU Flag" className="h-8" />
              </div>
              <h3 className="text-sm font-medium text-gray-900">VPS Châu Âu</h3>
            </Link>
            
            <Link to="/vps-canada" className="bg-white p-4 rounded-lg shadow text-center hover:shadow-md transition-shadow">
              <div className="h-12 flex items-center justify-center mb-2">
                <img src={getStorageUrl('flag/canada-flag.svg')} alt="Canada Flag" className="h-8" />
              </div>
              <h3 className="text-sm font-medium text-gray-900">VPS Canada</h3>
            </Link>
            
            <Link to="/vps-viet-nam" className="bg-white p-4 rounded-lg shadow text-center hover:shadow-md transition-shadow">
              <div className="h-12 flex items-center justify-center mb-2">
                <img src={getStorageUrl('flag/vietnam-flag.svg')} alt="Vietnam Flag" className="h-8" />
              </div>
              <h3 className="text-sm font-medium text-gray-900">VPS Việt Nam</h3>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <Link to="/vps-windows" className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                <Server className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">VPS Windows</h3>
                <p className="text-gray-600 text-sm">Hỗ trợ đa dạng hệ điều hành Windows Server</p>
              </div>
            </Link>
            
            <Link to="/vps-forex" className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                <Zap className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">VPS Forex</h3>
                <p className="text-gray-600 text-sm">Giải pháp tối ưu cho giao dịch forex</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Cam Kết Từ VMON Cloud</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Chất lượng - Uy tín - Hiệu quả - Tiết kiệm chi phí
          </p>
          <p className="text-lg mb-8 max-w-3xl mx-auto">
            Trung thực trong lời nói và hành động, chịu trách nhiệm với các vấn đề xảy ra.
          </p>
          <Link
            to="/contact"
            className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
          >
            Liên Hệ Ngay
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Cùng nhau, Trực tuyến.
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Chúng tôi ở đây giúp các bạn có thể làm việc online một cách hiệu quả nhất.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:support@vmon.vn"
              className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
            >
              Liên hệ ngay
            </a>
            <a
              href="tel:+84966805315"
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors"
            >
              Gọi điện tư vấn
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;